"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { ProductVariant } from "@/hooks/useFunnelState";
import { Info, Loader2, Pencil, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAgentId } from "@/context/AgentIdContext"; // Import useAgentId
import { deleteDocument } from "@/api/simpego";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { addYears, isAfter, isValid, format as formatDateFns, subDays, subYears } from "date-fns";
import * as Sentry from "@sentry/react";
import ChipNumberInput from "@/components/pet-details/ChipNumberInput";

interface ConfirmationPageProps {
  selectedProduct: ProductVariant;
  finalPrice: number;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string;
  contactPhone: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  zipCode: string;
  country: string;
  petType: "cat" | "dog";
  petName: string;
  breed: string | null;
  breedLabel: string | null;
  birthDate: Date;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown";
  isCastrated: boolean;
  isHouseCat: boolean;
  chipNumber: string | null;
  preexistingCoverage: boolean;
  worldwideCoverage: boolean;
  yearlyDeductible: number;
  coPayCoveragePercentage: number;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  firstDue: Date | null;
  dispatchContract: "customer" | "customer_and_agent" | "agent";
  dispatchInvoice: "customer" | "customer_and_agent" | "agent";
  dispatchReminder: "customer" | "customer_and_agent" | "agent";
  dispatchEmail: "customer" | "customer_and_agent" | "agent";
  agentDispatchEmail?: string | null;
  contractId: string | null;
  contractToken: string | null;
  consultationProtocolDocumentId: number | null;
  consultationProtocolFilename: string | null;
  consultationProtocolPendingFile: string | null;
  consultationProtocolPendingFilename: string | null;
  offerLoadedFromUrl: boolean;
  hasLeftConfirmationAfterOfferLoad: boolean;
  onSetConsultationProtocolDocument: (documentId: number, filename: string) => void;
  onClearConsultationProtocolDocument: () => void;
  onSetConsultationProtocolPending: (base64File: string, filename: string) => void;
  onClearConsultationProtocolPending: () => void;
  onStartNewQuote: () => void;
  onBack: () => void;
  onApplyOffer: (firstName: string, lastName: string) => void;
  onCreateOffer: (firstName: string, lastName: string) => void;
  isLoading: boolean;
  onEditPlanAndCoverage: () => void;
  onEditPetDetails: () => void;
  onEditPersonalDetails: () => void;
  onEditPayment: () => void;
  onChipNumberChange: (value: string | null) => void;
  customerGender?: "male" | "female" | null;
  contactBirthdate?: Date | undefined;
  selectedDogSize?: "small" | "large" | null;
  hideRequestOfferButton?: boolean;
}

const ConfirmationPage: React.FC<ConfirmationPageProps> = ({
  selectedProduct,
  finalPrice,
  contactFirstName,
  contactLastName,
  contactEmail,
  contactPhone,
  addressLine1,
  addressLine2,
  city,
  zipCode,
  country,
  petType,
  petName,
  breed,
  breedLabel,
  birthDate,
  selectedBreedType,
  gender,
  isCastrated,
  isHouseCat,
  chipNumber,
  preexistingCoverage,
  worldwideCoverage,
  yearlyDeductible,
  coPayCoveragePercentage,
  paymentInterval,
  paymentMethod,
  startOfInsurance,
  firstDue,
  dispatchContract,
  dispatchInvoice,
  dispatchReminder,
  dispatchEmail,
  agentDispatchEmail,
  contractId,
  contractToken,
  consultationProtocolDocumentId,
  consultationProtocolFilename,
  consultationProtocolPendingFile,
  consultationProtocolPendingFilename,
  offerLoadedFromUrl,
  hasLeftConfirmationAfterOfferLoad,
  onSetConsultationProtocolDocument,
  onClearConsultationProtocolDocument,
  onSetConsultationProtocolPending,
  onClearConsultationProtocolPending,
  onStartNewQuote,
  onBack,
  onApplyOffer,
  onCreateOffer,
  isLoading,
  onEditPlanAndCoverage,
  onEditPetDetails,
  onEditPersonalDetails,
  onEditPayment,
  onChipNumberChange,
  customerGender = null,
  contactBirthdate,
  selectedDogSize = null,
  hideRequestOfferButton = false,
}) => {
  const { t, i18n } = useTranslation();
  const { isCustomAgentId, agentToken } = useAgentId(); // Use the hook
  const petNameLabel = (petName || "").trim() || t("common.yourPet");
  const [documentsAccepted, setDocumentsAccepted] = React.useState(false);
  const [applyAttempted, setApplyAttempted] = React.useState(false);
  const [chipNumberError, setChipNumberError] = React.useState("");
  const showChipNumberInput = true;
  const chipNumberInputRef = React.useRef<HTMLInputElement>(null);
  const isAgentAuthenticated = !!agentToken;
  const [isDeletingDocument, setIsDeletingDocument] = React.useState(false);
  const [documentError, setDocumentError] = React.useState<string | null>(null);
  const documentInputRef = React.useRef<HTMLInputElement | null>(null);
  const isCopyMode = offerLoadedFromUrl && hasLeftConfirmationAfterOfferLoad;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const avbLink = t("common.avbLink") as string;
  const agbLink = t("common.agbLink") as string;
  const privacyLink = t("common.privacyPolicyLink") as string;

  const formatDate = (d: Date) => {
    try {
      return d.toLocaleDateString(i18n.language);
    } catch {
      return d.toISOString().split("T")[0];
    }
  };

  const endOfInsurance = React.useMemo(() => {
    if (!isValid(startOfInsurance)) return null;

    if (firstDue) {
      if (!isValid(firstDue)) {
        Sentry.captureMessage(`Invalid firstDueDate: ${firstDue}`);
        return null;
      }
      if (!isAfter(startOfInsurance, firstDue)) {
        const oneYearAfterStart = addYears(startOfInsurance, 1);
        if (isAfter(firstDue, oneYearAfterStart)) {
          return formatDateFns(subYears(firstDue, 1), "dd.MM.yyyy");
        }
        return formatDateFns(firstDue, "dd.MM.yyyy");
      }
      return formatDateFns(addYears(firstDue, 1), "dd.MM.yyyy");
    }

    return formatDateFns(subDays(addYears(startOfInsurance, 1), 1), "dd.MM.yyyy");
  }, [firstDue, startOfInsurance]);

  const yesNo = (v: boolean) => (v ? t("common.yes") : t("common.no"));

  const intervalLabel = (interval: 1 | 2 | 4 | 12) => {
    return t(`priceCustomization.paymentIntervalOptions.${interval}`);
  };

  const methodLabel = (method: "credit_card" | "ESR") => {
    return t(`priceCustomization.paymentMethodOptions.${method}`);
  };

  const validateChipNumber = (value: string | null) => !value || /^\d{15}$/.test(value);
  const ensureChipNumberValid = () => {
    if (validateChipNumber(chipNumber)) {
      setChipNumberError("");
      return true;
    }
    setChipNumberError(t("petDetails.validation.chipNumber"));
    if (chipNumberInputRef.current) {
      chipNumberInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      chipNumberInputRef.current.focus();
    }
    return false;
  };

  const canUploadDocument = isAgentAuthenticated;

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("File read failed"));
      reader.onload = () => {
        if (typeof reader.result !== "string") {
          reject(new Error("Invalid file data"));
          return;
        }
        const base64 = reader.result.split(",")[1];
        if (!base64) {
          reject(new Error("Empty file data"));
          return;
        }
        resolve(base64);
      };
      reader.readAsDataURL(file);
    });

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";
    if (!canUploadDocument) {
      setDocumentError(t("consultationProtocol.missingAgent"));
      return;
    }
    if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type)) {
      setDocumentError(t("consultationProtocol.errors.type"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setDocumentError(t("consultationProtocol.errors.size"));
      return;
    }
    if (consultationProtocolDocumentId) {
      setDocumentError(t("consultationProtocol.deleteFirst"));
      return;
    }
    setDocumentError(null);
    try {
      const base64File = await fileToBase64(file);
      onSetConsultationProtocolPending(base64File, file.name);
    } catch (error) {
      console.error("Document read failed:", error);
      setDocumentError(t("consultationProtocol.errors.readFailed"));
    }
  };

  const handleDocumentDelete = async () => {
    if (!canUploadDocument || !consultationProtocolDocumentId || !contractId || !contractToken) return;
    setIsDeletingDocument(true);
    setDocumentError(null);
    try {
      if (!isCopyMode) {
        await deleteDocument(contractToken!, contractId!, consultationProtocolDocumentId);
      }
      onClearConsultationProtocolDocument();
      onClearConsultationProtocolPending();
    } catch (error) {
      console.error("Document delete failed:", error);
      setDocumentError(t("consultationProtocol.errors.deleteFailed"));
    } finally {
      setIsDeletingDocument(false);
    }
  };

  const handlePendingDocumentDelete = async () => {
    setDocumentError(null);
    if (consultationProtocolDocumentId && contractId && contractToken && !isCopyMode) {
      setIsDeletingDocument(true);
      try {
        await deleteDocument(contractToken, contractId, consultationProtocolDocumentId);
        onClearConsultationProtocolDocument();
        onClearConsultationProtocolPending();
      } catch (error) {
        console.error("Document delete failed:", error);
        setDocumentError(t("consultationProtocol.errors.deleteFailed"));
      } finally {
        setIsDeletingDocument(false);
      }
      return;
    }
    onClearConsultationProtocolPending();
  };

  const handleDocumentPickerOpen = () => {
    documentInputRef.current?.click();
  };

  const SectionHeader: React.FC<{ title: string; onEdit: () => void }> = ({ title, onEdit }) => (
    <div className="flex items-center justify-between">
      <h3 className="text-xl font-semibold">{title}</h3>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Edit"
        onClick={onEdit}
        className="text-primary hover:text-primary/80"
      >
        <Pencil className="h-4 w-4" />
      </Button>
    </div>
  );

  const paidHint = t("pclosing-close-button-explanation") as string;

  const displayBreed = React.useMemo(() => {
    if (selectedBreedType === "mixed") {
      return breedLabel || t(`petDetails.breedType${selectedBreedType?.charAt(0).toUpperCase() + selectedBreedType?.slice(1)}`);
    }
    return breedLabel || (breed ? breed.replace(/_/g, " ") : "");
  }, [selectedBreedType, breedLabel, breed, t]);

  const documentAcceptanceTextKey = isCustomAgentId
    ? "confirmation.summary.agentAcceptPrefix" 
    : "confirmation.summary.acceptPrefix"; 

  const documentAcceptanceTextKeyAnd = isCustomAgentId
    ? "confirmation.summary.agentAnd"
    : "confirmation.summary.and";

  const documentAcceptanceTextKeyAnd2 = isCustomAgentId
    ? "confirmation.summary.agentAnd2"
    : "confirmation.summary.and2";

  const documentAcceptanceTextKeyPostfix = isCustomAgentId
    ? "confirmation.summary.agentAcceptPostfix" 
    : "confirmation.summary.acceptPostfix"; 

  const dispatchOptionLabel = (value: "customer" | "customer_and_agent" | "agent") => {
    if (value === "customer") return t("broker-display-customer");
    if (value === "customer_and_agent") return t("broker-display-customer-and-agent");
    return t("broker-display-agent");
  };


  return (
    <Card className="w-full max-w-4xl mx-auto border-none shadow-none">
      <CardHeader className="pb-0">
        <CardTitle className="text-3xl font-bold">{t("banner-header-4", { petName: petNameLabel })}</CardTitle>
        <p className="text-muted-foreground">{t("confirmation.summary.subtitle", { petName: petNameLabel })}</p>
      </CardHeader>
      <CardContent className="space-y-8">
        <div className="pt-6 border-t">
          <SectionHeader title={t("confirmation.summary.coverageHeader", { petName: petNameLabel })} onEdit={onEditPlanAndCoverage} />
          <div className="mt-4 space-y-2">
            <div className="flex items-baseline justify-between">
              <div className="font-semibold flex items-center gap-2">
                {selectedProduct.name}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("common.editPlan")}
                  onClick={onEditPlanAndCoverage}
                  className="h-6 w-6 text-primary hover:text-primary/80"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-primary font-bold">CHF {finalPrice.toFixed(2)} / {intervalLabel(paymentInterval)}</div>
            </div>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>{t("confirmation.summary.planTypeLabel")}: {selectedProduct.name}</li>
              <li>{t("common.yearlyDeductible")}: CHF {yearlyDeductible.toFixed(2)}</li>
              <li>{t("common.coPayCoverage")}: {coPayCoveragePercentage}%</li>
              <li>{t("confirmation.summary.preexistingLabel")}: {yesNo(preexistingCoverage)}</li>
              <li>{t("common.worldwideCoverage")}: {yesNo(worldwideCoverage)}</li>
            </ul>
          </div>
        </div>

        <div className="pt-6 border-t">
          <SectionHeader title={t("closing-pet-information-header", { petName: petNameLabel })} onEdit={onEditPetDetails} />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">{t("confirmation.summary.petTypeLabel")}:</span> {t(`petSelection.${petType}`)}</div>
            <div><span className="font-medium">{t("confirmation.summary.petNameLabel")}:</span> {petName}</div>
            <div><span className="font-medium">{t("confirmation.summary.petBreedLabel")}:</span> {displayBreed}</div>
            {petType === "dog" && selectedBreedType === "mixed" && selectedDogSize !== null && (
              <div><span className="font-medium">{t("confirmation.summary.petTallDogLabel")}:</span> {selectedDogSize === "large" ? t("common.yes") : t("common.no")}</div>
            )}
            <div><span className="font-medium">{t("confirmation.summary.petBirthdateLabel")}:</span> {formatDate(birthDate)}</div>
            <div><span className="font-medium">{t("confirmation.summary.petGenderLabel")}:</span> {t(`petDetails.gender${gender?.charAt(0).toUpperCase() + gender?.slice(1)}`)}</div>
            <div><span className="font-medium">{t("confirmation.summary.petCastratedLabel")}:</span> {yesNo(isCastrated)}</div>
            {petType === "cat" && (
              <div><span className="font-medium">{t("confirmation.summary.petHouseCatLabel")}:</span> {yesNo(isHouseCat)}</div>
            )}
            {chipNumber && !showChipNumberInput && (
              <div><span className="font-medium">{t("petDetails.chipNumberLabelOptional")}:</span> {chipNumber}</div>
            )}
          </div>
          {showChipNumberInput && (
            <div className="mt-4">
              <ChipNumberInput
                chipNumber={chipNumber || ""}
                onChipNumberChange={(value) => {
                  onChipNumberChange(value.trim() ? value : null);
                  setChipNumberError("");
                }}
                error={chipNumberError}
                isLoading={isLoading}
                inputRef={chipNumberInputRef}
                showHint={false}
              />
            </div>
          )}
        </div>

        <div className="pt-6 border-t">
          <SectionHeader title={t("closing-begin-and-pay-header")} onEdit={onEditPayment} />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">{t("confirmation.summary.paymentIntervalLabel")}:</span> {intervalLabel(paymentInterval)}</div>
            <div><span className="font-medium">{t("confirmation.summary.paymentMethodLabel")}:</span> {methodLabel(paymentMethod)}</div>
            <div><span className="font-medium">{t("confirmation.summary.startDateLabel")}:</span> {formatDate(startOfInsurance)}</div>
            {endOfInsurance && (
              <div><span className="font-medium">{t("confirmation.summary.endDateLabel")}:</span> {endOfInsurance}</div>
            )}
          </div>
        </div>

        <div className="pt-6 border-t">
          <SectionHeader title={t("confirmation.summary.aboutYouHeader")} onEdit={onEditPersonalDetails} />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div>
              <span className="font-medium">{t("confirmation.summary.yourSalutationLabel")}:</span>{" "}
              {customerGender
                ? t(`confirmation.summary.salutation.${customerGender}` as const)
                : t("confirmation.summary.yourSalutationPlaceholder")}
            </div>
            <div><span className="font-medium">{t("confirmation.summary.yourNameLabel")}:</span> {contactFirstName} {contactLastName}</div>
            <div><span className="font-medium">{t("confirmation.summary.yourEmailLabel")}:</span> {contactEmail}</div>
            {contactPhone && <div><span className="font-medium">{t("confirmation.summary.yourPhoneLabel")}:</span> {contactPhone}</div>}
            <div>
              <span className="font-medium">{t("confirmation.summary.yourBirthdateLabel")}:</span>{" "}
              {contactBirthdate ? formatDate(contactBirthdate) : t("common.notApplicable")}
            </div>
          </div>
          <div className="mt-4">
            <h4 className="text-base font-semibold">{t("confirmation.summary.yourAddressHeader")}</h4>
            <div className="mt-2 text-sm space-y-1">
              <div>{addressLine1} {addressLine2}</div>
              <div>{country}-{zipCode} {city}</div>
            </div>
          </div>
        </div>

        {isAgentAuthenticated && (
          <div className="pt-6 border-t">
            <SectionHeader title={t("contactDetails.dispatchControlTitle")} onEdit={onEditPersonalDetails} />
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div><span className="font-medium">{t("contactDetails.dispatchEmails")}:</span> {dispatchOptionLabel(dispatchEmail)}</div>
              <div><span className="font-medium">{t("contactDetails.dispatchContracts")}:</span> {dispatchOptionLabel(dispatchContract)}</div>
              <div><span className="font-medium">{t("contactDetails.dispatchInvoices")}:</span> {dispatchOptionLabel(dispatchInvoice)}</div>
              <div><span className="font-medium">{t("contactDetails.dispatchReminders")}:</span> {dispatchOptionLabel(dispatchReminder)}</div>
              {agentDispatchEmail && (
                <div><span className="font-medium">{t("contactDetails.agentEmailLabel")}:</span> {agentDispatchEmail}</div>
              )}
            </div>
          </div>
        )}

        {isAgentAuthenticated && (
          <div className="pt-6 border-t">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <h3 className="text-xl font-semibold">{t("consultationProtocol.input")}</h3>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                      <Info className="h-4 w-4 text-muted-foreground" />
                      <span className="sr-only">{t("common.info")}</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t("consultationProtocol.input")}</DialogTitle>
                      <DialogDescription className="whitespace-pre-line">
                        {t("consultationProtocol.tooltip")}
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="w-full space-y-1 sm:max-w-xl">
                {(consultationProtocolDocumentId || consultationProtocolPendingFile) && (
                  <div className="rounded-full border border-border/60 bg-background px-4 py-2 text-sm">
                    {consultationProtocolDocumentId
                      ? consultationProtocolFilename || t("consultationProtocol.fallbackFilename")
                      : consultationProtocolPendingFilename || t("consultationProtocol.fallbackFilename")}
                  </div>
                )}
                <div className="flex w-full flex-wrap items-center justify-between gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs"
                    onClick={handleDocumentPickerOpen}
                    disabled={!canUploadDocument}
                  >
                    {t("consultationProtocol.upload")}
                  </Button>
                  {consultationProtocolDocumentId ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-xs"
                      onClick={handleDocumentDelete}
                      disabled={isDeletingDocument}
                    >
                      {isDeletingDocument && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {!isDeletingDocument && <X className="h-4 w-4" aria-hidden="true" />}
                      {t("consultationProtocol.delete")}
                    </Button>
                  ) : consultationProtocolPendingFile ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-xs"
                      onClick={handlePendingDocumentDelete}
                      disabled={isDeletingDocument}
                    >
                      {isDeletingDocument && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {!isDeletingDocument && <X className="h-4 w-4" aria-hidden="true" />}
                      {t("consultationProtocol.clear")}
                    </Button>
                  ) : null}
                </div>
                {!canUploadDocument && (
                  <p className="text-xs text-muted-foreground">
                    {t("consultationProtocol.missingAgent")}
                  </p>
                )}
                {documentError && <p className="text-sm text-destructive">{documentError}</p>}
              </div>
            </div>
            <input
              ref={documentInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              className="hidden"
              onChange={handleDocumentUpload}
            />
          </div>
        )}

        <Card className="bg-secondary/10 dark:bg-secondary/20 border-secondary/30 dark:border-secondary/40 shadow-none">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-lg font-semibold">
              {t("closing-application-questions-big-header")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 text-sm text-muted-foreground">
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>{t("closing-application-question-1")}</li>
                  <li>{t("closing-application-question-2")}</li>
                  <li>{t("closing-application-question-3")}</li>
                  <li>{t("closing-application-question-4")}</li>
                  <li>{t("closing-application-question-5")}</li>
                  <li>{t("closing-application-question-6")}</li>
                  <li>{t("closing-application-question-7")}</li>
                </ul>
          </CardContent>
        </Card>

        <div className="pt-6 border-t">
          <div className="flex items-start gap-3">
            <Checkbox
              id="docs-accepted"
              checked={documentsAccepted}
              onCheckedChange={(v) => setDocumentsAccepted(!!v)}
              className="mt-1"
            />
            <Label htmlFor="docs-accepted" className="text-sm leading-relaxed">
              {t(documentAcceptanceTextKey)}{" "}
              <a
                href={avbLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-4 hover:opacity-80"
              >
                {t("common.avb")}
              </a>
              {t(documentAcceptanceTextKeyAnd)}
              <a
                href={agbLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-4 hover:opacity-80"
              >
                {t("common.agb")}
              </a>
              {t(documentAcceptanceTextKeyAnd2)}
              <a
                  href={privacyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-4 hover:opacity-80"
              >
                {t("common.privacyPolicy")}
              </a>
              {t(documentAcceptanceTextKeyPostfix)}
            </Label>
          </div>
          {!documentsAccepted && applyAttempted && (
            <p className="text-sm text-destructive mt-2">{t("confirmation.pleaseConfirm")}</p>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex flex-col gap-3">
        <>
          <Button
            onClick={() => {
              if (isLoading) return;
              if (!documentsAccepted) {
                setApplyAttempted(true);
                return;
              }
              if (!ensureChipNumberValid()) return;
              onApplyOffer(contactFirstName || "", contactLastName || "");
            }}
            className="w-full bg-continue-button text-continue-button-foreground hover:bg-continue-button/90"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("confirmation.applyOffer")}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-[-0.5rem]">{paidHint}</p>
        </>
        {!hideRequestOfferButton && (
          <Button
            onClick={() => {
              if (!ensureChipNumberValid()) return;
              onCreateOffer(contactFirstName || "", contactLastName || "");
            }}
            className="w-full"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("contactDetails.requestOffer")}
          </Button>
        )}
        <Button variant="outline" onClick={onBack} className="w-full" disabled={isLoading}>
          {t("contactDetails.back")}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ConfirmationPage;
